import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const WEEKDAYS = [
  { value: 1, ar: 'الأحد',     en: 'Sun' },
  { value: 2, ar: 'الإثنين',   en: 'Mon' },
  { value: 4, ar: 'الثلاثاء',  en: 'Tue' },
  { value: 8, ar: 'الأربعاء',  en: 'Wed' },
  { value: 16, ar: 'الخميس',   en: 'Thu' },
  { value: 32, ar: 'الجمعة',   en: 'Fri' },
  { value: 64, ar: 'السبت',    en: 'Sat' },
];

const VSMContractForm = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const navigate = useNavigate();

  const T = {
    title:           { ar: '➕ إنشاء عقد جديد', en: '➕ New Contract' },
    contractInfo:    { ar: 'بيانات العقد', en: 'Contract Information' },
    contractTitle:   { ar: 'عنوان العقد', en: 'Contract Title' },
    titlePlaceholder:{ ar: 'مثال: عقد غسيل شركة ABC', en: 'e.g. ABC Company Wash Agreement' },
    startDate:       { ar: 'تاريخ البداية', en: 'Start Date' },
    endDate:         { ar: 'تاريخ النهاية', en: 'End Date' },
    endDateHint:     { ar: 'اتركه فارغ لعقد مفتوح', en: 'Leave empty for open-ended' },
    totalValue:      { ar: 'القيمة الإجمالية (اختياري)', en: 'Total Value (optional)' },
    notes:           { ar: 'ملاحظات', en: 'Notes' },

    services:        { ar: 'الخدمات والتكرار', en: 'Services & Frequency' },
    addService:      { ar: '+ إضافة خدمة', en: '+ Add Service' },
    selectService:   { ar: 'اختر الخدمة...', en: 'Select service...' },
    frequency:       { ar: 'التكرار', en: 'Frequency' },
    daily:           { ar: 'يومي', en: 'Daily' },
    weekly:          { ar: 'أسبوعي', en: 'Weekly' },
    customWeekdays:  { ar: 'أيام محددة', en: 'Custom Days' },
    monthly:         { ar: 'شهري', en: 'Monthly' },
    oneTime:         { ar: 'مرة واحدة', en: 'One Time' },
    weekday:         { ar: 'يوم الأسبوع', en: 'Day of Week' },
    weekdays:        { ar: 'الأيام', en: 'Days' },
    monthDay:        { ar: 'يوم الشهر', en: 'Day of Month' },
    preferredTime:   { ar: 'الوقت المفضل', en: 'Preferred Time' },
    pricePerVisit:   { ar: 'السعر/زيارة', en: 'Price/Visit' },
    removeService:   { ar: '🗑️', en: '🗑️' },

    clients:         { ar: 'العملاء المشمولين بالعقد', en: 'Clients in This Contract' },
    selectAll:       { ar: 'تحديد الكل', en: 'Select All' },
    deselectAll:     { ar: 'إلغاء الكل', en: 'Deselect All' },
    searchClients:   { ar: 'ابحث عن عميل...', en: 'Search clients...' },
    selected:        { ar: 'محدد', en: 'selected' },
    allVehicles:     { ar: 'كل السيارات', en: 'All Vehicles' },
    specificVehicles:{ ar: 'سيارات محددة', en: 'Specific Vehicles' },
    autoApplyHint:   { ar: '✨ السيارات الجديدة تُضاف تلقائياً', en: '✨ New vehicles will be auto-added' },
    selectVehicles:  { ar: 'اختر السيارات...', en: 'Select vehicles...' },
    noVehicles:      { ar: 'لا توجد سيارات لهذا العميل', en: 'No vehicles for this client' },

    save:            { ar: '💾 إنشاء العقد', en: '💾 Create Contract' },
    saving:          { ar: 'جاري الحفظ...', en: 'Saving...' },
    cancel:          { ar: 'إلغاء', en: 'Cancel' },

    summary:         { ar: 'ملخص العقد', en: 'Contract Summary' },
    totalClients:    { ar: 'عميل', en: 'client(s)' },
    totalServices:   { ar: 'خدمة', en: 'service(s)' },

    errStartDate:    { ar: 'تاريخ البداية مطلوب', en: 'Start date required' },
    errServices:     { ar: 'يجب إضافة خدمة واحدة على الأقل', en: 'At least one service required' },
    errClients:      { ar: 'يجب اختيار عميل واحد على الأقل', en: 'At least one client required' },
    saveError:       { ar: 'فشل إنشاء العقد', en: 'Failed to create contract' },
    saveSuccess:     { ar: 'تم إنشاء العقد بنجاح', en: 'Contract created successfully' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  // ===== Contract header =====
  const today = new Date().toISOString().slice(0, 10);
  const [contractData, setContractData] = useState({
    title: '',
    start_date: today,
    end_date: '',
    status: 'active',
    total_value: '',
    notes: ''
  });

  // ===== Services =====
  const [services, setServices] = useState([
    {
      service_type_id: '',
      frequency: 'daily',
      weekdays_mask: 0,
      monthly_day: 1,
      preferred_time: '08:00',
      price_per_visit: ''
    }
  ]);

  // ===== Clients =====
  const [allClients, setAllClients] = useState([]);
  const [allServiceTypes, setAllServiceTypes] = useState([]);
  const [vehiclesByClient, setVehiclesByClient] = useState({});
  const [clientSearch, setClientSearch] = useState('');
  // Map: clientId -> { selected, applyToAll, vehicleIds }
  const [clientAssignments, setClientAssignments] = useState({});

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch clients + service types
  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      try {
        const [cRes, sRes] = await Promise.all([
          fetch(`${API}/clients?limit=500`, { headers }),
          fetch(`${API}/service-types?is_active=1`, { headers })
        ]);
        const cData = await cRes.json();
        const sData = await sRes.json();
        if (cData.success) setAllClients(cData.data);
        if (sData.success) setAllServiceTypes(sData.data);
      } catch (err) {
        console.error('[VSM] Fetch initial data error:', err);
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  // Fetch vehicles for a client (lazy)
  const fetchClientVehicles = useCallback(async (clientId) => {
    if (vehiclesByClient[clientId]) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/vehicles?client_id=${clientId}&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setVehiclesByClient(prev => ({ ...prev, [clientId]: data.data }));
      }
    } catch (err) {
      console.error('[VSM] Fetch vehicles error:', err);
    }
  }, [vehiclesByClient]);

  // ===== Service handlers =====
  const updateService = (idx, field, value) => {
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addService = () => {
    setServices(prev => [...prev, {
      service_type_id: '',
      frequency: 'daily',
      weekdays_mask: 0,
      monthly_day: 1,
      preferred_time: '08:00',
      price_per_visit: ''
    }]);
  };

  const removeService = (idx) => {
    if (services.length === 1) return;
    setServices(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleWeekdayBit = (idx, bit) => {
    const current = services[idx].weekdays_mask || 0;
    const newMask = current & bit ? current & ~bit : current | bit;
    updateService(idx, 'weekdays_mask', newMask);
  };

  // ===== Client handlers =====
  const toggleClient = (clientId) => {
    setClientAssignments(prev => {
      const exists = prev[clientId];
      if (exists?.selected) {
        // Deselect
        const { [clientId]: _, ...rest } = prev;
        return rest;
      } else {
        // Select with default = applyToAll
        return {
          ...prev,
          [clientId]: { selected: true, applyToAll: true, vehicleIds: [] }
        };
      }
    });
  };

  const setApplyMode = (clientId, applyToAll) => {
    setClientAssignments(prev => ({
      ...prev,
      [clientId]: { ...prev[clientId], applyToAll, vehicleIds: applyToAll ? [] : (prev[clientId]?.vehicleIds || []) }
    }));
    if (!applyToAll) fetchClientVehicles(clientId);
  };

  const toggleVehicle = (clientId, vehicleId) => {
    setClientAssignments(prev => {
      const cur = prev[clientId] || { selected: true, applyToAll: false, vehicleIds: [] };
      const ids = cur.vehicleIds.includes(vehicleId)
        ? cur.vehicleIds.filter(id => id !== vehicleId)
        : [...cur.vehicleIds, vehicleId];
      return { ...prev, [clientId]: { ...cur, vehicleIds: ids } };
    });
  };

  const filteredClients = allClients.filter(c => {
    if (!clientSearch) return true;
    const s = clientSearch.toLowerCase();
    return (c.name && c.name.toLowerCase().includes(s)) ||
           (c.name_ar && c.name_ar.includes(clientSearch)) ||
           (c.phone && c.phone.includes(s));
  });

  const selectAllClients = () => {
    const newAssignments = {};
    filteredClients.forEach(c => {
      newAssignments[c.id] = { selected: true, applyToAll: true, vehicleIds: [] };
    });
    setClientAssignments(prev => ({ ...prev, ...newAssignments }));
  };

  const deselectAllClients = () => setClientAssignments({});

  const selectedCount = Object.values(clientAssignments).filter(a => a.selected).length;

  // ===== Submit =====
  const handleSubmit = async () => {
    if (!contractData.start_date) { alert(tr('errStartDate')); return; }
    if (services.length === 0 || services.some(s => !s.service_type_id)) {
      alert(tr('errServices')); return;
    }
    if (selectedCount === 0) { alert(tr('errClients')); return; }

    setSaving(true);
    const token = localStorage.getItem('token');

    const payload = {
      title: contractData.title || null,
      start_date: contractData.start_date,
      end_date: contractData.end_date || null,
      status: contractData.status,
      total_value: contractData.total_value ? parseFloat(contractData.total_value) : null,
      notes: contractData.notes || null,
      services: services.map(s => ({
        service_type_id: parseInt(s.service_type_id, 10),
        frequency: s.frequency,
        weekdays_mask: s.frequency === 'custom_weekdays' ? s.weekdays_mask : null,
        monthly_day: s.frequency === 'monthly' ? parseInt(s.monthly_day, 10) : null,
        preferred_time: s.preferred_time || null,
        price_per_visit: s.price_per_visit ? parseFloat(s.price_per_visit) : null
      })),
      client_assignments: Object.entries(clientAssignments)
        .filter(([_, a]) => a.selected)
        .map(([clientId, a]) => ({
          client_id: parseInt(clientId, 10),
          apply_to_all_vehicles: !!a.applyToAll,
          vehicle_ids: a.applyToAll ? [] : a.vehicleIds
        }))
    };

    try {
      const res = await fetch(`${API}/contracts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        alert(`${tr('saveSuccess')}\n\n${data.data.contract_number}\n${data.data.items_created} items`);
        navigate(`/vsm/contracts/${data.data.id}`);
      } else {
        alert(data.error || tr('saveError'));
      }
    } catch (err) {
      console.error('[VSM] Submit error:', err);
      alert(tr('saveError'));
    }
    setSaving(false);
  };

  // ===== Styles =====
  const inputStyle = {
    width: '100%', padding: '10px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: '11px', color: '#94a3b8', fontWeight: '600',
    display: 'block', marginBottom: '4px'
  };
  const sectionStyle = {
    background: 'rgba(15,23,42,0.5)', borderRadius: '12px',
    padding: '20px', marginBottom: '16px',
    border: '1px solid rgba(255,255,255,0.08)'
  };
  const sectionTitle = (icon, text) => (
    <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#f1f5f9',
      margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px',
      paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      {icon} {text}
    </h3>
  );

  if (loading) {
    return <Layout><div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{tr('loading') || 'Loading...'}</div></Layout>;
  }

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1200px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
            {tr('title')}
          </h1>
          <button
            onClick={() => navigate('/vsm/contracts')}
            style={{
              background: 'rgba(100,116,139,0.2)', color: '#cbd5e1',
              border: '1px solid rgba(100,116,139,0.3)',
              padding: '8px 16px', borderRadius: '8px',
              fontSize: '13px', cursor: 'pointer', fontWeight: '600'
            }}
          >{tr('cancel')}</button>
        </div>

        {/* SUMMARY BAR */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
          border: '1px solid rgba(99,102,241,0.3)',
          borderRadius: '10px', padding: '12px 18px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>{tr('summary')}:</span>
          <span style={{ color: '#a5b4fc', fontSize: '14px', fontWeight: '700' }}>
            👥 {selectedCount} {tr('totalClients')}
          </span>
          <span style={{ color: '#6ee7b7', fontSize: '14px', fontWeight: '700' }}>
            🧰 {services.filter(s => s.service_type_id).length} {tr('totalServices')}
          </span>
        </div>

        {/* SECTION 1: Contract Info */}
        <div style={sectionStyle}>
          {sectionTitle('📄', tr('contractInfo'))}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>{tr('contractTitle')}</label>
              <input
                value={contractData.title}
                onChange={e => setContractData(p => ({ ...p, title: e.target.value }))}
                style={inputStyle}
                placeholder={tr('titlePlaceholder')}
              />
            </div>
            <div>
              <label style={labelStyle}>{tr('startDate')} *</label>
              <input
                type="date"
                value={contractData.start_date}
                onChange={e => setContractData(p => ({ ...p, start_date: e.target.value }))}
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>{tr('endDate')}</label>
              <input
                type="date"
                value={contractData.end_date}
                onChange={e => setContractData(p => ({ ...p, end_date: e.target.value }))}
                style={inputStyle}
              />
              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '3px' }}>{tr('endDateHint')}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginTop: '12px' }}>
            <div>
              <label style={labelStyle}>{tr('totalValue')}</label>
              <input
                type="number"
                step="0.01"
                value={contractData.total_value}
                onChange={e => setContractData(p => ({ ...p, total_value: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>{tr('notes')}</label>
              <input
                value={contractData.notes}
                onChange={e => setContractData(p => ({ ...p, notes: e.target.value }))}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: Services */}
        <div style={sectionStyle}>
          {sectionTitle('🧰', tr('services'))}
          {services.map((svc, idx) => (
            <div key={idx} style={{
              background: 'rgba(0,0,0,0.2)', borderRadius: '10px',
              padding: '14px', marginBottom: '10px',
              border: '1px solid rgba(255,255,255,0.05)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 40px', gap: '10px', alignItems: 'flex-end' }}>
                <div>
                  <label style={labelStyle}>{tr('selectService')} *</label>
                  <select
                    value={svc.service_type_id}
                    onChange={e => updateService(idx, 'service_type_id', e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    required
                  >
                    <option value="">{tr('selectService')}</option>
                    {allServiceTypes.map(s => (
                      <option key={s.id} value={s.id}>
                        {isRTL ? s.name_ar : s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{tr('frequency')}</label>
                  <select
                    value={svc.frequency}
                    onChange={e => updateService(idx, 'frequency', e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="daily">{tr('daily')}</option>
                    <option value="weekly">{tr('weekly')}</option>
                    <option value="custom_weekdays">{tr('customWeekdays')}</option>
                    <option value="monthly">{tr('monthly')}</option>
                    <option value="one_time">{tr('oneTime')}</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{tr('preferredTime')}</label>
                  <input
                    type="time"
                    value={svc.preferred_time}
                    onChange={e => updateService(idx, 'preferred_time', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{tr('pricePerVisit')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={svc.price_per_visit}
                    onChange={e => updateService(idx, 'price_per_visit', e.target.value)}
                    style={inputStyle}
                    placeholder="—"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => removeService(idx)}
                    disabled={services.length === 1}
                    style={{
                      background: services.length === 1 ? 'rgba(100,116,139,0.15)' : 'rgba(239,68,68,0.15)',
                      color: services.length === 1 ? '#475569' : '#fca5a5',
                      border: `1px solid ${services.length === 1 ? 'rgba(100,116,139,0.2)' : 'rgba(239,68,68,0.25)'}`,
                      padding: '10px', borderRadius: '8px', cursor: services.length === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px', height: '38px'
                    }}
                  >🗑️</button>
                </div>
              </div>

              {/* Conditional: weekdays selector */}
              {svc.frequency === 'custom_weekdays' && (
                <div style={{ marginTop: '12px' }}>
                  <label style={labelStyle}>{tr('weekdays')}</label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {WEEKDAYS.map(day => {
                      const active = (svc.weekdays_mask || 0) & day.value;
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleWeekdayBit(idx, day.value)}
                          style={{
                            padding: '8px 14px', borderRadius: '8px',
                            border: active ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.15)',
                            background: active ? 'rgba(99,102,241,0.25)' : 'rgba(15,23,42,0.5)',
                            color: active ? '#a5b4fc' : '#94a3b8',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            minWidth: '70px'
                          }}
                        >
                          {isRTL ? day.ar : day.en}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Conditional: monthly day */}
              {svc.frequency === 'monthly' && (
                <div style={{ marginTop: '12px', maxWidth: '180px' }}>
                  <label style={labelStyle}>{tr('monthDay')}</label>
                  <input
                    type="number"
                    min="1" max="28"
                    value={svc.monthly_day}
                    onChange={e => updateService(idx, 'monthly_day', e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addService}
            style={{
              background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
              border: '1px dashed rgba(99,102,241,0.4)',
              padding: '10px 16px', borderRadius: '8px',
              fontSize: '13px', cursor: 'pointer', fontWeight: '600',
              marginTop: '6px'
            }}
          >{tr('addService')}</button>
        </div>

        {/* SECTION 3: Clients (multi-select) */}
        <div style={sectionStyle}>
          {sectionTitle('👥', `${tr('clients')} (${selectedCount} ${tr('selected')})`)}

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              placeholder={tr('searchClients')}
              style={{ ...inputStyle, maxWidth: '300px' }}
            />
            <button
              type="button"
              onClick={selectAllClients}
              style={{
                background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
                border: '1px solid rgba(16,185,129,0.3)',
                padding: '8px 14px', borderRadius: '8px',
                fontSize: '12px', cursor: 'pointer', fontWeight: '600'
              }}
            >✓ {tr('selectAll')}</button>
            <button
              type="button"
              onClick={deselectAllClients}
              style={{
                background: 'rgba(239,68,68,0.1)', color: '#fca5a5',
                border: '1px solid rgba(239,68,68,0.2)',
                padding: '8px 14px', borderRadius: '8px',
                fontSize: '12px', cursor: 'pointer', fontWeight: '600'
              }}
            >✕ {tr('deselectAll')}</button>
          </div>

          <div style={{
            maxHeight: '450px', overflow: 'auto',
            background: 'rgba(0,0,0,0.2)', borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.05)'
          }}>
            {filteredClients.map(client => {
              const assignment = clientAssignments[client.id];
              const isSelected = !!assignment?.selected;
              return (
                <div
                  key={client.id}
                  style={{
                    padding: '12px 14px',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    background: isSelected ? 'rgba(99,102,241,0.08)' : 'transparent',
                    transition: 'background 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleClient(client.id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => toggleClient(client.id)}>
                      <div style={{ color: '#f1f5f9', fontWeight: '600', fontSize: '13px' }}>
                        {isRTL && client.name_ar ? client.name_ar : client.name}
                      </div>
                      {client.phone && (
                        <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }} dir="ltr">
                          {client.phone} {client.vehicle_count > 0 && `• 🚙 ${client.vehicle_count}`}
                        </div>
                      )}
                    </div>
                  </div>

                  {isSelected && (
                    <div style={{ marginTop: '10px', marginInlineStart: '30px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setApplyMode(client.id, true)}
                          style={{
                            padding: '5px 12px', borderRadius: '6px',
                            border: assignment.applyToAll ? '2px solid #10b981' : '2px solid rgba(255,255,255,0.1)',
                            background: assignment.applyToAll ? 'rgba(16,185,129,0.15)' : 'rgba(15,23,42,0.5)',
                            color: assignment.applyToAll ? '#6ee7b7' : '#94a3b8',
                            fontSize: '11px', cursor: 'pointer', fontWeight: '600'
                          }}
                        >
                          ✓ {tr('allVehicles')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setApplyMode(client.id, false)}
                          style={{
                            padding: '5px 12px', borderRadius: '6px',
                            border: !assignment.applyToAll ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.1)',
                            background: !assignment.applyToAll ? 'rgba(99,102,241,0.15)' : 'rgba(15,23,42,0.5)',
                            color: !assignment.applyToAll ? '#a5b4fc' : '#94a3b8',
                            fontSize: '11px', cursor: 'pointer', fontWeight: '600'
                          }}
                        >
                          🎯 {tr('specificVehicles')}
                        </button>
                        {assignment.applyToAll && (
                          <span style={{ fontSize: '10px', color: '#10b981', fontStyle: 'italic' }}>
                            {tr('autoApplyHint')}
                          </span>
                        )}
                      </div>

                      {!assignment.applyToAll && (
                        <div style={{ marginTop: '8px' }}>
                          {!vehiclesByClient[client.id] ? (
                            <div style={{ color: '#64748b', fontSize: '11px' }}>{tr('loading') || 'Loading...'}</div>
                          ) : vehiclesByClient[client.id].length === 0 ? (
                            <div style={{ color: '#fca5a5', fontSize: '11px' }}>{tr('noVehicles')}</div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {vehiclesByClient[client.id].map(v => {
                                const checked = assignment.vehicleIds.includes(v.id);
                                return (
                                  <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => toggleVehicle(client.id, v.id)}
                                    style={{
                                      padding: '5px 10px', borderRadius: '6px',
                                      border: checked ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.15)',
                                      background: checked ? 'rgba(99,102,241,0.2)' : 'rgba(15,23,42,0.5)',
                                      color: checked ? '#a5b4fc' : '#94a3b8',
                                      fontSize: '11px', cursor: 'pointer',
                                      fontFamily: 'monospace', fontWeight: '700'
                                    }}
                                  >
                                    {checked ? '✓ ' : ''}{v.plate_letters_en} {v.plate_numbers}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
          <button
            type="button"
            onClick={() => navigate('/vsm/contracts')}
            style={{
              background: 'rgba(100,116,139,0.2)', color: '#cbd5e1',
              border: '1px solid rgba(100,116,139,0.3)',
              padding: '12px 24px', borderRadius: '8px',
              fontSize: '14px', cursor: 'pointer', fontWeight: '600'
            }}
          >{tr('cancel')}</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            style={{
              background: saving ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              color: '#fff', border: 'none',
              padding: '12px 28px', borderRadius: '8px',
              fontSize: '14px', cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '700'
            }}
          >{saving ? tr('saving') : tr('save')}</button>
        </div>
      </div>
    </Layout>
  );
};

export default VSMContractForm;
